import React, {useCallback, useMemo} from 'react';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {useNavigate} from 'shared/reload/helper/useNavigate';
import {formatPrice} from 'shared/helpers';
import {PartialJob} from 'shared/types/PartialJob';
import type {OptionsType, TableButton} from 'shared/types';
import {SidebarProduct} from 'shared/components/Header/entity/SidebarProduct';
import {useFetchSidebarProducts} from 'shared/components/Header/helper/useFetchSidebarProducts';
import {useProductActions} from 'shared/components/Header/helper/useProductActions';
import {useAppSelector} from 'store/customer';
import {useSearchParams} from 'react-router-dom';
import {isProductDataLoadingState} from 'components/customer/Product/store/productSlice';

export const useProductTableConfig = () => {
    const {job, room, isPageDirty, actionButtonsEnabled, isJobProcessing} =
        useJobContext() as PartialJob;
    const {userProfile} = useAppContext();
    const navigate = useNavigate();
    const addProductUrl = `/v2/job/${job.displayId}/room/${room.id}/product`;
    const addRoomUrl = `/v2/job/${job.displayId}/room`;
    const {products, isFetching} = useFetchSidebarProducts();
    const isProductDataLoading = useAppSelector(isProductDataLoadingState);
    const [searchParams] = useSearchParams();
    const {
        dialog,
        cabinetId,
        hasUnsavedProductChanges,
        confirmDiscardProductChanges,
        removeCabinet,
        editCabinet,
        copyCabinet,
        editQFP,
    } = useProductActions();
    const isCopied = searchParams.has('copy');

    const nameFormat = (value: string, row: SidebarProduct) => {
        if (cabinetId && cabinetId == row.id && !isCopied) {
            return (
                <strong>
                    {`${row.quantity} x ${row.note ? row.note : value}`}
                </strong>
            );
        }

        return `${row.quantity} x ${row.note ? row.note : value}`;
    };

    const dimensionFormat = (value: string, row: SidebarProduct) => {
        if (cabinetId && cabinetId == row.id && !isCopied) {
            return <strong>{`${row.dimensions}`}</strong>;
        }

        return `${row.dimensions}`;
    };

    const priceFormat = (value: number, row: SidebarProduct) => {
        if (cabinetId && cabinetId == row.id && !isCopied) {
            return <strong>{formatPrice(value, row)}</strong>;
        }

        return String(formatPrice(value, row));
    };

    const linkNavigationHandler = useCallback(
        (url: string) => () => {
            void navigate(url);
        },
        []
    );

    const noRoomAndVariationProducts = (room: number, variations: number) => {
        if (!room && variations === 0) {
            return (
                <>
                    Please click{' '}
                    <span
                        style={{color: 'red', cursor: 'pointer'}}
                        onClick={linkNavigationHandler(addRoomUrl)}>
                        <strong>Add A Room </strong>
                    </span>{' '}
                    before adding products.
                </>
            );
        } else if (room && variations > 0) {
            return <>No Products.</>;
        } else {
            return (
                <>
                    No Products.{' '}
                    {job.displayId && isJobProcessing ? (
                        <span
                            style={{color: 'red', cursor: 'pointer'}}
                            onClick={linkNavigationHandler(addProductUrl)}>
                            <strong>Click Add Product</strong> to add one.
                        </span>
                    ) : null}
                </>
            );
        }
    };

    const tableConfig = useMemo<OptionsType>(() => {
        if (room) {
            const config: OptionsType = {
                fields: [
                    {
                        fieldName: 'productNumber',
                        title: '#',
                        format: function formatProductNumber(
                            productNumber: string,
                            row: SidebarProduct
                        ) {
                            if (cabinetId && cabinetId == row.id && !isCopied) {
                                return <strong>{productNumber}</strong>;
                            }
                            return productNumber;
                        },
                    },
                    {
                        fieldName: 'typeName',
                        title: 'Current Products',
                        format: nameFormat,
                    },
                    {
                        fieldName: 'dimensionString',
                        title: 'Dimensions',
                        format: dimensionFormat,
                        width: 80,
                    },
                ],
                data: isFetching ? undefined : products,
                noRecordsText: (
                    <>
                        {noRoomAndVariationProducts(
                            room.id,
                            job.variationsConfirmed
                        )}
                    </>
                ),
            };

            if (userProfile?.show_pricing) {
                config.fields.push({
                    fieldName: 'cost',
                    title: 'Cost',
                    width: 65,
                    format: function productFormatPrice(
                        value: number,
                        row: SidebarProduct
                    ) {
                        return (
                            <div
                                style={
                                    job.status == 5 ? {textAlign: 'center'} : {}
                                }>
                                {priceFormat(value, row)}
                            </div>
                        );
                    },
                });
            }

            if (job.status != 5) {
                let buttons: TableButton[] = [];
                if (
                    !userProfile?.isAddProductAvailable &&
                    userProfile?.isQFPAvailable
                ) {
                    buttons = [
                        {
                            iconName: 'Options-Edit.svg',
                            title: 'Edit Product',
                            action: editQFP,
                        },
                        {
                            iconName: 'Options-Delete.svg',
                            title: 'Delete Product',
                            action: removeCabinet,
                            confirmChanges: isPageDirty,
                        },
                    ];
                } else if (userProfile?.isAddProductAvailable) {
                    buttons = [
                        {
                            iconName: 'Options-Edit.svg',
                            disabled: isProductDataLoading,
                            title: 'Edit Product',
                            action: (row: SidebarProduct, event) =>
                                editCabinet(row, false, row.isQFP, event),
                        },
                        {
                            iconName: 'Options-Copy.svg',
                            disabled: isProductDataLoading,
                            title: 'Copy Product',
                            action: copyCabinet,
                            isHidden: (row: SidebarProduct) => row.isQFP,
                        },
                        {
                            iconName: 'Options-Delete.svg',
                            title: 'Delete Product',
                            action: removeCabinet,
                            confirmChanges: isPageDirty,
                        },
                    ];
                }

                config.options = {
                    title: '',
                    width: 75,
                    buttons,
                };
            }

            if (!actionButtonsEnabled) {
                config.options = undefined;
            }

            return config;
        } else {
            return undefined;
        }
    }, [
        products,
        room,
        job,
        userProfile,
        isPageDirty,
        actionButtonsEnabled,
        cabinetId,
        navigate,
        isFetching,
        isProductDataLoading,
        isCopied,
        hasUnsavedProductChanges,
        confirmDiscardProductChanges,
    ]);

    return {
        tableConfig,
        dialog,
        isFetching,
    };
};
